import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { Loader2, Check, X } from 'lucide-react'
import {
  zanshinService,
  ZanshinApprovalItem,
} from '@/services/zanshin.service'
import { ApiError } from '@/services/api'

/**
 * ZanshinApprovalsPanel
 * ─────────────────────
 * Lista delle decisioni Zanshin in attesa di approvazione (modalita' MANUAL
 * per pair). Polling ogni 5s su /zanshin/approvals (silent).
 *
 * - Countdown locale decrementato ogni 1s da `seconds_left` dell'ultimo poll.
 * - Auto-rimozione della card quando il countdown arriva a 0 (TTL 60s scaduto;
 *   il poll successivo confermera' la sparizione).
 * - Approva / Rifiuta: optimistic UI con rimozione immediata; toast per esito.
 * - Errore 410 Gone (expired/op_not_active) → toast soft, niente errore rosso:
 *   e' una scadenza naturale, non un crash.
 *
 * Si nasconde completamente quando non ci sono proposte (no clutter UI).
 */
export function ZanshinApprovalsPanel() {
  const [items, setItems] = useState<ZanshinApprovalItem[]>([])
  const [tickNonce, setTickNonce] = useState(0)  // forza re-render countdown
  const [busy, setBusy] = useState<Record<number, 'approve' | 'reject' | undefined>>({})
  // baseTime: ms quando il server ha risposto. Usato per calcolare il countdown
  // locale senza driftare se il poll e' lento.
  const baseTimeRef = useRef<number>(Date.now())
  // Pending action set: id di proposte appena approvate/rifiutate localmente.
  // Filtrato dalle response di poll per evitare "resurrezione" della card
  // mentre il backend non ha ancora propagato lo stato (race poll vs azione).
  // Entry rimossa dopo ~10s (safety net se il backend rispondesse mai PENDING).
  const inFlightRef = useRef<Map<number, number>>(new Map())  // id -> timestamp ms

  const cleanInFlight = () => {
    const now = Date.now()
    for (const [id, ts] of inFlightRef.current) {
      if (now - ts > 10_000) inFlightRef.current.delete(id)
    }
  }

  const refresh = useCallback(async () => {
    try {
      const resp = await zanshinService.listApprovals()
      baseTimeRef.current = Date.now()
      cleanInFlight()
      setItems(resp.items.filter(it => !inFlightRef.current.has(it.id)))
    } catch {
      /* silent: il pannello e' una vista accessoria, non interrompiamo */
    }
  }, [])

  // Polling 5s + countdown 1s
  useEffect(() => {
    refresh()
    const pollId = setInterval(refresh, 5000)
    const tickId = setInterval(() => setTickNonce(n => (n + 1) % 1000), 1000)
    return () => {
      clearInterval(pollId)
      clearInterval(tickId)
    }
  }, [refresh])

  const markInFlight = (id: number) => {
    inFlightRef.current.set(id, Date.now())
  }

  const handleApprove = async (item: ZanshinApprovalItem) => {
    setBusy(b => ({ ...b, [item.id]: 'approve' }))
    markInFlight(item.id)
    setItems(prev => prev.filter(x => x.id !== item.id))  // optimistic
    try {
      const res = await zanshinService.approveRequest(item.id)
      if (res.ok) {
        toast.success('Decisione approvata', {
          description: item.summary ?? `Op #${item.operation_id}`,
        })
      } else {
        toast.warning('Approvazione non completata', { description: res.error ?? '' })
      }
    } catch (err: any) {
      // 410 Gone = expired/op_not_active → toast soft, non errore
      if (err instanceof ApiError && err.status === 410) {
        toast.info('Proposta scaduta', {
          description: 'Il motore valutera di nuovo al prossimo ciclo.',
        })
      } else {
        toast.error('Errore approvazione', {
          description: err?.detail ?? err?.message ?? '',
        })
      }
    } finally {
      setBusy(b => { const n = { ...b }; delete n[item.id]; return n })
    }
  }

  const handleReject = async (item: ZanshinApprovalItem) => {
    setBusy(b => ({ ...b, [item.id]: 'reject' }))
    markInFlight(item.id)
    setItems(prev => prev.filter(x => x.id !== item.id))  // optimistic
    try {
      const res = await zanshinService.rejectRequest(item.id)
      if (res.ok) {
        toast.success('Decisione rifiutata')
      } else {
        toast.warning('Rifiuto non completato', { description: res.error ?? '' })
      }
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 410) {
        toast.info('Proposta scaduta')
      } else {
        toast.error('Errore rifiuto', {
          description: err?.detail ?? err?.message ?? '',
        })
      }
    } finally {
      setBusy(b => { const n = { ...b }; delete n[item.id]; return n })
    }
  }

  // Countdown locale per item (consuma tickNonce per re-render).
  // Re-render forzato ogni 1s anche se tickNonce non serve esplicitamente.
  void tickNonce
  const now = Date.now()
  const visible = items
    .map(it => {
      const elapsed = Math.floor((now - baseTimeRef.current) / 1000)
      const left = Math.max(0, (it.seconds_left ?? 0) - elapsed)
      return { ...it, _left: left }
    })
    .filter(it => it._left > 0)

  if (visible.length === 0) return null

  return (
    <div className="bg-[#0d1529] border border-amber-700/60 rounded-xl p-4 mb-4 shadow-[0_0_18px_rgba(245,158,11,0.12)]">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_8px_#fbbf24] animate-pulse" />
        <span className="text-[11px] font-bold tracking-widest uppercase text-amber-300">
          Zanshin — proposte in attesa
        </span>
        <span className="ml-auto text-[10px] text-[#64748b]">
          {visible.length} {visible.length === 1 ? 'proposta' : 'proposte'} · scade entro 60s
        </span>
      </div>

      <div className="space-y-2">
        {visible.map(item => {
          const left = item._left
          const pct = Math.max(0, Math.min(100, (left / 60) * 100))
          const barColor =
            left > 30 ? 'bg-emerald-500' :
            left > 10 ? 'bg-amber-500' : 'bg-red-500'
          const b = busy[item.id]
          return (
            <div
              key={item.id}
              className="bg-[#0a0f1e] border border-[#1e3a5f]/60 rounded-lg p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-[#64748b] font-mono mb-0.5">
                    Op #{item.operation_id} · pair #{item.pair_id}
                  </div>
                  <div className="text-sm text-white font-medium leading-snug break-words">
                    {item.summary || item.action || 'Decisione Zanshin'}
                  </div>
                  {item.reason && (
                    <div className="text-[11px] text-[#94a3b8] mt-1 break-words">
                      {item.reason}
                    </div>
                  )}
                  {/* Countdown bar */}
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#1e3a5f]/60 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${barColor} transition-all duration-1000 ease-linear`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={`text-[10px] font-mono font-bold tabular-nums w-7 text-right ${
                      left > 30 ? 'text-emerald-400' :
                      left > 10 ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      {left}s
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => handleApprove(item)}
                    disabled={!!b}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-700/30 border border-emerald-600/60 text-emerald-300 text-xs font-bold hover:bg-emerald-700/50 disabled:opacity-40 transition-all"
                  >
                    {b === 'approve' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Approva
                  </button>
                  <button
                    onClick={() => handleReject(item)}
                    disabled={!!b}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-900/30 border border-red-800/60 text-red-300 text-xs font-bold hover:bg-red-900/50 disabled:opacity-40 transition-all"
                  >
                    {b === 'reject' ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    Rifiuta
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
